import { type CSSProperties } from "react";
import { useNavigate } from "react-router";
import { Panel, PanelRow } from "./Panel";
import { EmptyState, Pill, showToast } from "../primitives";
import { PatternMemoryCard } from "./PatternMemoryCard";
import { useAgentLearnings } from "../../hooks/use-agent-learnings";
import { useFindPatterns } from "../../hooks/use-pattern-actions";

/**
 * Sprint 21 — the Agent cockpit Learnings panel. Per-agent mindset visibility:
 * how many durable learnings the agent has accumulated, the recent
 * retrospectives, and the high-confidence patterns it has formed. Supervisor
 * surface — Kuvio sells visibility of the workforce getting better over time.
 */

function formatRelative(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const days = Math.floor(diff / 86_400_000);
  if (days < 1) return "today";
  if (days === 1) return "yesterday";
  if (days < 30) return `${days}d ago`;
  return `${Math.floor(days / 30)}mo ago`;
}

const healthStyle: CSSProperties = {
  display: "flex",
  flexWrap: "wrap",
  gap: 14,
  padding: "8px 12px",
  fontSize: 12,
  color: "var(--color-text-secondary)",
};

const subHeadStyle: CSSProperties = {
  fontSize: 11,
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: 0.4,
  color: "var(--color-text-tertiary)",
  padding: "4px 12px 0",
};

function Stat({ value, label }: { value: number; label: string }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "baseline", gap: 4 }}>
      <strong style={{ fontSize: 15, color: "var(--color-text-primary)" }}>{value}</strong>
      {label}
    </span>
  );
}

export function LearningsPanel({ nodeId }: { nodeId: string }) {
  const navigate = useNavigate();
  const learnings = useAgentLearnings(nodeId);
  const findPatterns = useFindPatterns();

  const isEmpty =
    !learnings.isLoading &&
    learnings.feedbackMemories.length === 0 &&
    learnings.retrospectives.length === 0;

  function handleFindPatterns() {
    findPatterns.mutate(
      { agent_id: nodeId },
      {
        onSuccess: () => {
          showToast("Pattern detection complete");
          learnings.refetch();
        },
        onError: (e) => showToast(e instanceof Error ? e.message : "Pattern detection failed", "error", true),
      },
    );
  }

  return (
    <Panel
      title="Learnings"
      count={learnings.feedbackMemories.length}
      action={
        <button
          onClick={handleFindPatterns}
          disabled={findPatterns.isPending}
          style={{
            border: "var(--kuvio-border-default)",
            background: "transparent",
            color: "var(--color-text-secondary)",
            cursor: "pointer",
            fontSize: 11,
            fontWeight: 500,
            padding: "3px 8px",
            borderRadius: "var(--border-radius-sm)",
            fontFamily: "var(--font-sans)",
          }}
        >
          {findPatterns.isPending ? "Finding…" : "Find patterns"}
        </button>
      }
      isLoading={learnings.isLoading}
      isEmpty={isEmpty}
      empty={
        <EmptyState>
          No learnings yet. As this agent completes priorities, retrospectives and feedback memories
          accumulate here.
        </EmptyState>
      }
    >
      {/* Mindset Health */}
      <div style={healthStyle}>
        <Stat value={learnings.highConfidenceCount} label="reliable learnings" />
        <Stat value={learnings.patterns.length} label="patterns" />
        <Stat value={learnings.retrospectives.length} label="retrospectives" />
      </div>

      {/* Recent retrospectives */}
      {learnings.retrospectives.length > 0 && (
        <>
          <div style={subHeadStyle}>Recent retrospectives</div>
          {learnings.retrospectives.slice(0, 5).map((p) => (
            <PanelRow key={p.id} onClick={() => navigate(`/library/${p.id}`)} title={`Open ${p.name}`}>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 8, minWidth: 0, flex: 1 }}>
                <Pill variant="blue" icon="history">retro</Pill>
                <span style={nameStyle}>{p.name}</span>
                <span style={{ fontSize: 11, color: "var(--color-text-tertiary)", marginLeft: "auto", whiteSpace: "nowrap" }}>
                  {formatRelative(p.updated_at)}
                </span>
              </span>
            </PanelRow>
          ))}
        </>
      )}

      {/* Recent patterns */}
      {learnings.patterns.length > 0 && (
        <>
          <div style={subHeadStyle}>Patterns</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8, padding: "4px 12px 8px" }}>
            {learnings.patterns.slice(0, 3).map((m) => (
              <PatternMemoryCard key={m.id} memory={m} />
            ))}
          </div>
        </>
      )}
    </Panel>
  );
}

const nameStyle: CSSProperties = {
  fontSize: 13,
  fontWeight: 500,
  color: "var(--color-text-primary)",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  minWidth: 0,
};
